/**
 * Pure builders for the dashboard's system-status pills and event feed.
 *
 * Everything here derives from data the app already collects — an
 * InsightReport plus per-platform sync statuses — so the dashboard gains no
 * new source of truth, only a summary of the existing ones. Pure on purpose:
 * boundaries, severity order and the cap are the parts that can be wrong, and
 * they are all testable without I/O.
 *
 * @module services/dashboard/dashboardEvents
 */

import { MOVER_SEVERE_CHANGE, STALE_AFTER_DAYS } from "../insights/insightConstants";
import type { InsightReport } from "../insights/insightTypes";

/** The feed stays scannable: the five loudest items, never a scroll of them. */
export const MAX_DASHBOARD_EVENTS = 5;

/** One platform's sync state, already reduced from its accounts. */
export interface PlatformSyncStatus {
  platform: string;
  /** ISO timestamp of the freshest successful sync; null = never synced. */
  lastSyncedAt: string | null;
  /**
   * Newest metric DATE this platform actually holds (YYYY-MM-DD), or null when
   * it holds none.
   *
   * This, not lastSyncedAt, is what "is my data current" means. The two come
   * apart in both directions: last_synced_at is stamped only by the queue
   * worker, so a sync run any other way (a backfill script, a manual
   * orchestrator call) leaves it untouched and the strip cries stale over data
   * that arrived minutes ago; and a job that completes having fetched nothing
   * stamps it fresh over data that has not moved in a week.
   */
  dataThrough: string | null;
  /** Last sync failure message, if the most recent attempt failed. */
  syncError: string | null;
}

/**
 * Platforms that carry no spend, and so are not channels.
 *
 * Google Analytics measures a website; it never buys a placement. It cannot
 * donate or receive budget, its conversions count the whole site rather than
 * anything an ad caused — on the audited workspace roughly 21,000 a day against
 * the ad platforms' hundreds — and /mmm excludes it from the mix by name for
 * exactly those reasons.
 *
 * The dashboard listed it in the same row as Facebook and Google Ads, which
 * invited the comparison the rest of the product is careful to avoid. It stays
 * in the strip, because whether it is syncing is a real question with a real
 * answer, but it is grouped and named rather than ranked beside the channels.
 */
export const MEASUREMENT_PLATFORMS = new Set(['analytics']);

export interface SyncPill {
  platform: string;
  /**
   * `ad` buys placements; `measurement` only observes. The strip separates
   * them, and nothing should rank one against the other.
   */
  kind: 'ad' | 'measurement';
  /** err = last attempt failed · warn = whatever ageSource measured — the data,
   *  or for a platform holding none, the last sync — is older than the shared
   *  3-day line · ok = fresh · never = no sync has ever completed. */
  state: "ok" | "warn" | "err" | "never";
  ageDays: number | null;
  /**
   * What ageDays was measured from, so the pill can word it truthfully:
   * 'data' = the platform's newest metric date (the normal case);
   * 'sync' = its newest last_synced_at — the fallback for a platform that
   * holds no metric rows, the only case where "synced" is the true word;
   * null = no date at all (state 'never').
   */
  ageSource: "data" | "sync" | null;
  error: string | null;
}

export type DashboardEventKind =
  | "noConv"
  | "cpaJump"
  | "syncFailed"
  | "unmeasurable"
  | "syncStale"
  | "stopped"
  | "accumulating";

export interface DashboardEvent {
  /** 1 = act now, 2 = money/data at risk, 3 = worth a look, 4 = informational. */
  severity: 1 | 2 | 3 | 4;
  source: "insights" | "sync";
  kind: DashboardEventKind;
  /** Where acting on this event actually happens. */
  link: "/insights" | "/settings/integrations";
  data: Record<string, string | number | string[]>;
}

const MS_PER_DAY = 86_400_000;

const ageInDays = (iso: string, todayISO: string): number =>
  Math.floor((Date.parse(todayISO) - Date.parse(iso.slice(0, 10))) / MS_PER_DAY);

/**
 * One pill per platform: the freshest account wins, any error wins the state.
 * The warn boundary reuses the insights 3-day line so the whole app draws
 * "stale" in the same place.
 *
 * Age is measured from the DATA, not from the job. `lastSyncedAt` is the
 * fallback only for a platform holding no metrics at all, where there is no
 * data date to measure and "when did we last try" is the best answer available.
 */
export const buildSyncPills = (statuses: PlatformSyncStatus[], todayISO: string): SyncPill[] => {
  const byPlatform = new Map<string, PlatformSyncStatus[]>();
  for (const s of statuses) {
    const list = byPlatform.get(s.platform);
    if (list) list.push(s); else byPlatform.set(s.platform, [s]);
  }

  return [...byPlatform.entries()].map(([platform, list]) => {
    const error = list.find((s) => s.syncError)?.syncError ?? null;
    const newestData = list
      .map((s) => s.dataThrough)
      .filter((d): d is string => d !== null)
      .sort()
      .pop() ?? null;
    const lastAttempt = list
      .map((s) => s.lastSyncedAt)
      .filter((d): d is string => d !== null)
      .sort()
      .pop() ?? null;
    const measuredFrom = newestData ?? lastAttempt;
    const ageDays = measuredFrom ? ageInDays(measuredFrom, todayISO) : null;
    // Which of the two dates above answered, so the strip can say "data
    // through" where it means the data and "synced" only where it means the job.
    const ageSource = ageDays === null ? null : newestData !== null ? ("data" as const) : ("sync" as const);

    const kind = MEASUREMENT_PLATFORMS.has(platform) ? ('measurement' as const) : ('ad' as const);

    if (error) return { platform, kind, state: "err" as const, ageDays, ageSource, error };
    if (ageDays === null) return { platform, kind, state: "never" as const, ageDays, ageSource, error: null };
    return {
      platform,
      kind,
      state: ageDays > STALE_AFTER_DAYS ? ("warn" as const) : ("ok" as const),
      ageDays,
      ageSource,
      error: null,
    };
  });
};

/**
 * Every event, loudest first, with no cap. Home's urgent count reads this one,
 * so it counts every act-now item: the events list that capped it at five was
 * retired (2026-10-14). Sorting is stable within a severity: the order below is
 * the order events were pushed, which follows the report's own arrays —
 * deterministic, no clock, no randomness.
 */
export const rankDashboardEvents = (
  report: InsightReport | null,
  pills: SyncPill[],
): DashboardEvent[] => {
  const events: DashboardEvent[] = [];

  if (report) {
    /**
     * Folded by ad NAME, not by ad id — and the name itself is left exactly as
     * the platform reports it. This groups rows; it does not rewrite them.
     *
     * One creative exists as many ad rows, one per ad set carrying it: on the
     * audited workspace the busiest is eleven. Keyed on the id, this feed
     * listed the same ad name twice out of only five slots, with byte-identical
     * text — a fifth of the panel spent saying nothing new. A reader acts on
     * the creative, which they know by its name, so that is what one row stands
     * for.
     */
    const folded = new Map<string, { event: DashboardEvent; worst: number; placements: number }>();

    for (const m of report.movers) {
      if (m.direction !== "worse") continue;

      // Bound to a local so the null check narrows it — `change === null` is
      // the "spent, converted nothing" case, not a missing figure.
      const change = m.change;
      // Below the severe line a CPA move is not dashboard-worthy at all; it
      // still belongs on /insights.
      if (change !== null && change < MOVER_SEVERE_CHANGE) continue;

      const kind: DashboardEventKind = change === null ? "noConv" : "cpaJump";
      const event: DashboardEvent = change === null
        ? { severity: 1, source: "insights", kind: "noConv", link: "/insights",
            data: { adName: m.adName, platform: m.platform } }
        : { severity: 1, source: "insights", kind: "cpaJump", link: "/insights",
            data: { adName: m.adName, platform: m.platform,
                    prior: Math.round(m.cpaPrior ?? 0), recent: Math.round(m.cpaRecent ?? 0),
                    pct: Math.round(change * 100) } };

      // Platform stays in the key: the same creative running on two platforms
      // is two facts, and merging them would hide one of them.
      const key = `${kind}:${m.platform}:${m.adName}`;
      const prior = folded.get(key);
      if (!prior) {
        folded.set(key, { event, worst: change ?? 0, placements: 1 });
        continue;
      }
      folded.set(key, {
        // Quote the worst placement. Which one that is moves day to day; the
        // row it stands for does not.
        event: (change ?? 0) > prior.worst ? event : prior.event,
        worst: Math.max(prior.worst, change ?? 0),
        placements: prior.placements + 1,
      });
    }

    for (const f of folded.values()) {
      // Said out loud rather than silently collapsed: "worst of six placements"
      // is a different instruction from "this creative is broken".
      events.push(
        f.placements > 1
          ? { ...f.event, data: { ...f.event.data, placements: f.placements } }
          : f.event,
      );
    }
  }

  for (const p of pills) {
    if (p.state === "err") {
      events.push({ severity: 1, source: "sync", kind: "syncFailed", link: "/settings/integrations",
        data: { platform: p.platform, error: p.error ?? "" } });
    }
  }

  if (report) {
    // Unmeasurable money, one event per platform with the groups summed —
    // the objective split lives on /insights; the dashboard states the size.
    const unByPlatform = new Map<string, number>();
    for (const u of report.unmeasurable) {
      unByPlatform.set(u.platform, (unByPlatform.get(u.platform) ?? 0) + u.spend);
    }
    for (const [platform, spend] of unByPlatform) {
      events.push({ severity: 2, source: "insights", kind: "unmeasurable", link: "/insights",
        data: { platform, spend: Math.round(spend) } });
    }
  }

  // Stale platforms grouped into ONE event: "google / analytics ค้าง 9 วัน"
  // reads as one problem, because operationally it is one (the sync stopped).
  // Data-aged pills only: a platform with no metric rows has no "newest data"
  // to be old, so it cannot make this event's sentence true. Its amber
  // "stale Nd" pill still shows it in the strip.
  const stale = pills.filter((p) => p.state === "warn" && p.ageSource === "data");
  if (stale.length > 0) {
    events.push({ severity: 2, source: "sync", kind: "syncStale", link: "/settings/integrations",
      data: { platforms: stale.map((p) => p.platform), days: Math.max(...stale.map((p) => p.ageDays ?? 0)) } });
  }

  if (report) {
    for (const s of report.stopped) {
      events.push({ severity: 3, source: "insights", kind: "stopped", link: "/insights",
        data: { adName: s.adName, platform: s.platform, date: s.lastSpendDate ?? "" } });
    }

    const accumulatingTotal = report.accumulating.reduce((n, a) => n + a.adCount, 0);
    if (accumulatingTotal > 0) {
      events.push({ severity: 4, source: "insights", kind: "accumulating", link: "/insights",
        data: { count: accumulatingTotal } });
    }
  }

  return events.sort((a, b) => a.severity - b.severity);
};

/**
 * The same ranking, cut to its loudest MAX_DASHBOARD_EVENTS. Its one caller is
 * /looker's attention box, which shows the first three of these.
 */
export const buildDashboardEvents = (
  report: InsightReport | null,
  pills: SyncPill[],
): DashboardEvent[] => rankDashboardEvents(report, pills).slice(0, MAX_DASHBOARD_EVENTS);
